/**
 * Core Lazy Loading Utilities
 * Base functionality for lazy component loading
 */

import React, { lazy, ComponentType } from 'react';

// Types for lazy component system
export interface LazyComponentConfig {
  preloadDelay?: number;
  fallbackComponent?: ComponentType;
  errorBoundary?: ComponentType<{ children: React.ReactNode }>;
  preloadTrigger?: 'hover' | 'visible' | 'idle' | 'interaction';
}

// Enhanced lazy loading with error handling and preloading
export function createLazyComponent<T = Record<string, unknown>>(
  importFn: () => Promise<{ default: ComponentType<T> } | ComponentType<T>>,
  config: LazyComponentConfig = {}
): ComponentType<T> {
  const LazyComponent = lazy(async () => {
    try {
      const module = await importFn();
      return {
        default: 'default' in module ? module.default : (module as ComponentType<T>)
      };
    } catch (_error) {
      // eslint-disable-next-line no-console
      // console.error('Failed to load component:', error);
      // Return a fallback component if loading fails
      return {
        default: config.fallbackComponent || (() => 
          React.createElement('div', {
            className: 'error-fallback p-4 text-red-600'
          }, 'Component failed to load')
        ) as ComponentType<T>
      };
    }
  });

  // Add preloading capability
  const preloadableLazy = LazyComponent as unknown as ComponentType<T> & { preload: () => Promise<{ default: ComponentType<T> } | ComponentType<T>> };
  preloadableLazy.preload = importFn;
  
  return preloadableLazy as ComponentType<T> & { preload: () => Promise<ComponentType<T> | { default: ComponentType<T> }> };
}